import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {DynamicStatusButton} from 'shells/button/dynamic-status-button/dynamic-status-button';
import {MainContentContainer} from 'shells/layout/main-content-container';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {appHistory} from '../../../history';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {PageTitleBar} from '../../layout';
import {useGetTeamsIntegration} from '../../scheduler/location/conferencing-modals/graphql/hooks/use-get-teams-integration';
import {useSetMicrosoftTeamsIsEnabled} from '../../scheduler/location/conferencing-modals/graphql/hooks/use-set-microsoft-teams-is-enabled';

import {
    getFlashBannerType,
    getPopoverContent,
    getServiceUrl,
    getStatusDescription,
    getStatusText,
    handleEnableTeamsIntegration,
} from '../helpers';
import {PageContent} from '../page-content';

type Props = ContextRouter;

export function TeamsListPage(props: Props) {
    const [errorMessage, setErrorMesssage] = React.useState<string | null | undefined>(null);
    const {microsoftTeamsIntegration, isLoading, refetch, error} = useGetTeamsIntegration();
    const {user} = useGetSessionUser();

    const microsoftTeamsUserEmail = microsoftTeamsIntegration
        ? microsoftTeamsIntegration.email
        : '';
    const isMicrosoftTeamsConnected = microsoftTeamsIntegration
        ? microsoftTeamsIntegration.isEnabled
        : false;
    const setMicrosoftTeamsIsEnabled = useSetMicrosoftTeamsIsEnabled();

    const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

    const connectMicrosoftTeams = (onStart?: () => void, onSuccess?: () => void): void => {
        handleEnableTeamsIntegration({
            setMicrosoftTeamsIsEnabled,
            refetch,
            setError: setErrorMesssage,
            onAuthStart: onStart,
            onAuthSuccess: onSuccess,
        }).then((hasAuth) => {
            if (!hasAuth) {
                setMicrosoftTeamsIsEnabled({isEnabled: false});
            }
        });
    };

    const disconnectMicrosoftTeams = () => {
        setMicrosoftTeamsIsEnabled({isEnabled: false});
    };

    const handleReconfigure = () => {
        setMicrosoftTeamsIsEnabled({isEnabled: false}).then(() => {
            appHistory.replace(getServiceUrl('MICROSOFT_TEAMS'));
        });
    };

    return (
        <MainContentContainer whiteBackground={true}>
            <PageTitleBar
                title='Microsoft Teams'
                subheaderPrimaryText={
                    isMicrosoftTeamsConnected
                        ? 'View meeting details, transcriptions, and summaries all in one place'
                        : 'Connect Nutshell to your Microsoft Teams account for seamless collaboration'
                }
                buttons={[
                    NutClientConfig.hasMicrosoftTeamsTranscription && isMicrosoftTeamsConnected && (
                        <DynamicStatusButton
                            key='zoom'
                            text={getStatusText(
                                isMicrosoftTeamsConnected,
                                error,
                                'MICROSOFT_TEAMS'
                            )}
                            headerType={getFlashBannerType(isMicrosoftTeamsConnected, error)}
                            headerContent={getStatusDescription(
                                isMicrosoftTeamsConnected,
                                error,
                                'MICROSOFT_TEAMS'
                            )}
                            isDialogOpen={isDialogOpen}
                            setIsDialogOpen={setIsDialogOpen}
                            popoverContent={getPopoverContent({
                                isEnabled: true,
                                errorMessage: error,
                                userEmail: microsoftTeamsUserEmail,
                                handleReconfigure,
                                setIsDialogOpen,
                                serviceType: 'MICROSOFT_TEAMS',
                            })}
                        />
                    ),
                ]}
            />
            {!microsoftTeamsIntegration && isLoading ? (
                <LoadingPage />
            ) : (
                <PageContent
                    errorMessage={errorMessage}
                    setError={setErrorMesssage}
                    refetch={refetch}
                    isConnected={isMicrosoftTeamsConnected}
                    isEnabled={true}
                    userEmail={microsoftTeamsUserEmail || ''}
                    isLoadingIntegration={isLoading}
                    serviceType='MICROSOFT_TEAMS'
                    user={user}
                    enableIntegration={connectMicrosoftTeams}
                    disableIntegration={disconnectMicrosoftTeams}
                    connectIntegration={connectMicrosoftTeams}
                    {...props}
                />
            )}
        </MainContentContainer>
    );
}
